import React from "react";
import { View, Text, Pressable, Linking } from "react-native";
import { MapPin, Clock, Phone, Mail, ExternalLink } from "lucide-react-native";
import type {
  Clinic,
  ClinicDayHours,
  ClinicSchedule,
} from "@louise/clinics/clinics.types";
import { EMBER } from "../../../onboarding/theme";
import { getClinicPhoneContact } from "./clinicPhone";
import {
  useTranslations,
  type TranslationFunction,
  formatTimeHHMM,
  useLocale,
} from "@src/packages/i18n";
import { getOrganizationCountryLabel } from "@louise/providers/countryMetadata";
import {
  CompactActionButton,
  EmptyStateSubtitleText,
  Tag,
} from "@louise/moon-ui";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";
function getDays(
  t: TranslationFunction,
): { key: keyof Omit<ClinicSchedule, "utcOffset">; label: string }[] {
  return [
    { key: "mon", label: t("access.location_card.day_mon") },
    { key: "tue", label: t("access.location_card.day_tue") },
    { key: "wed", label: t("access.location_card.day_wed") },
    { key: "thu", label: t("access.location_card.day_thu") },
    { key: "fri", label: t("access.location_card.day_fri") },
    { key: "sat", label: t("access.location_card.day_sat") },
    { key: "sun", label: t("access.location_card.day_sun") },
  ];
}

const DAY_INDEX_TO_KEY: (keyof Omit<ClinicSchedule, "utcOffset">)[] = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
];

function countryLabel(country: string | undefined | null) {
  if (!country) return "";
  return getOrganizationCountryLabel(country);
}

function isOpenNow(hours: ClinicDayHours): boolean {
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = hours.open.split(":").map(Number);
  const [ch, cm] = hours.close.split(":").map(Number);
  return (
    nowMin >= (oh ?? 0) * 60 + (om ?? 0) && nowMin < (ch ?? 0) * 60 + (cm ?? 0)
  );
}

export type ClinicLocationMapPreviewProps = {
  addressQuery: string;
  addressLabel: string;
  onPress: () => void;
  openMapsLabel: string;
};

export function OpenMapsPill({
  onPress,
  label,
}: {
  onPress: () => void;
  label: string;
}) {
  const capturePress = usePressCapture("OpenMapsPill");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label), "open_maps");
        onPress();
      }}
      style={{
        position: "absolute",
        bottom: 8,
        right: 8,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        backgroundColor: semanticColors.surface.default,
        borderRadius: 8,
        paddingHorizontal: 8,
        paddingVertical: 4,
      }}
    >
      <ExternalLink size={10} color={EMBER.colors.textMid} />
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.textMid, fontSize: 10 }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

type Props = {
  clinic: Clinic;
  MapPreview: React.ComponentType<ClinicLocationMapPreviewProps>;
};

export default function ClinicLocationCardBase({ clinic, MapPreview }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const DAYS = getDays(t);
  const location = clinic.location;
  const address = clinic.address;

  const fullAddress = [
    address.address1,
    address.address2,
    address.city,
    address.state,
    address.postalCode,
    countryLabel(address.country),
  ]
    .filter(Boolean)
    .join(", ");

  const addressQuery =
    location != null
      ? `${location.latitude},${location.longitude}`
      : fullAddress ||
        [
          clinic.name,
          address.city,
          address.state,
          countryLabel(address.country),
        ]
          .filter(Boolean)
          .join(", ");

  if (!addressQuery) return null;

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressQuery)}`;
  const schedule = clinic.schedule;
  const todayKey = DAY_INDEX_TO_KEY[new Date().getDay()];
  const todayHours = todayKey && schedule ? schedule[todayKey] : undefined;
  const openNow = todayHours ? isOpenNow(todayHours) : false;
  const phoneContact = getClinicPhoneContact(
    clinic.phone,
    clinic.address.country,
  );

  const openMaps = () => Linking.openURL(mapsUrl);

  return (
    <View style={{ gap: 16 }}>
      <MapPreview
        addressQuery={addressQuery}
        addressLabel={fullAddress || clinic.name}
        onPress={openMaps}
        openMapsLabel={t("access.location_card.open_in_maps")}
      />

      {/* Address. Information only; the map above opens Maps. */}
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <MapPin
          size={15}
          color={EMBER.colors.textMid}
          style={{ marginTop: 1 }}
        />
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
        >
          {fullAddress}
        </Text>
      </View>

      {/* Hours */}
      <View style={{ gap: 8 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Clock size={15} color={EMBER.colors.textMid} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.text,
            }}
          >
            {t("access.location_card.opening_hours")}
          </Text>
          {openNow && (
            <Tag label={t("access.location_card.open_now")} tone="green" />
          )}
        </View>
        {schedule ? (
          <View style={{ paddingLeft: 25, gap: 2 }}>
            {DAYS.map(({ key, label }) => {
              const hours = schedule[key];
              const isToday = key === todayKey;
              return (
                <View
                  key={key}
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      width: 30,
                      color: isToday
                        ? EMBER.colors.text
                        : EMBER.colors.textFaint,
                      fontWeight: isToday ? "600" : "400",
                    }}
                  >
                    {label}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      color: isToday
                        ? EMBER.colors.text
                        : EMBER.colors.textFaint,
                    }}
                  >
                    {hours
                      ? `${formatTimeHHMM(hours.open, locale)} - ${formatTimeHHMM(hours.close, locale)}`
                      : t("access.location_card.closed")}
                  </Text>
                </View>
              );
            })}
          </View>
        ) : (
          <EmptyStateSubtitleText style={{ paddingLeft: 25, marginTop: 0 }}>
            {t("access.location_card.no_hours")}
          </EmptyStateSubtitleText>
        )}
      </View>

      {/* Contact */}
      {(phoneContact || clinic.email) && (
        <View style={{ flexDirection: "row", gap: 10 }}>
          {phoneContact && (
            <CompactActionButton
              label={phoneContact.display}
              action="call_clinic"
              variant="secondary"
              size="md"
              leadingIcon={Phone}
              flexGrow={1}
              flexBasis={0}
              onPress={() => {
                void Linking.openURL(phoneContact.href);
              }}
            />
          )}
          {clinic.email && (
            <CompactActionButton
              label={clinic.email}
              action="email_clinic"
              variant="secondary"
              size="md"
              leadingIcon={Mail}
              flexGrow={1}
              flexBasis={0}
              numberOfLines={1}
              textStyle={{ flexShrink: 1 }}
              onPress={() => {
                const subject = encodeURIComponent(
                  t("access.location_card.email_subject", {
                    params: { clinic: clinic.name },
                  }),
                );
                const body = encodeURIComponent(
                  t("access.location_card.email_body", {
                    params: { clinic: clinic.name },
                  }),
                );
                Linking.openURL(
                  `mailto:${clinic.email}?subject=${subject}&body=${body}`,
                );
              }}
            />
          )}
        </View>
      )}
    </View>
  );
}
